import { useCallback } from 'react';

import { Native, NativePostActionArgList } from '@givebacks/utils';

import { usePlatformService } from './usePlatformService';

type PlatformShareArgs = NativePostActionArgList['share']['data'];

const usePlatformShare = () => {
  const Platform = usePlatformService();

  /**
   * Share a page with the native share sheet
   * @param share_options - The options to share the page with
   * @param callback - The callback to call when the share is complete
   */
  const platformShare = useCallback(
    async ({ share_options }: PlatformShareArgs, callback: () => void) => {
      if (Platform.isDesktopBrowser) {
        return callback();
      }

      try {
        if (Platform.isNative) {
          return Native.postAction('share', { data: { share_options } });
        }
        const { url, message, title } = share_options;
        if (navigator.canShare({ url, text: message, title })) {
          return navigator.share({ url, text: message, title });
        } else {
          return callback();
        }
      } catch {
        return callback();
      }
    },
    [Platform]
  );

  /**
   * Share a cause page
   * @param url - The URL of the cause page
   * @param callback - The callback to call when the share is complete
   */
  const platformShareCause = useCallback(
    async ({ url, causeName }: { url: string; causeName: string }, callback: () => void) => {
      if (Platform.isDesktopBrowser) {
        return callback();
      }
      try {
        if (Platform.isNative) {
          return Native.postAction('share_cause', {
            data: { share_cause_options: { url, cause_name: causeName } },
          });
        }
        if (navigator.canShare({ url })) {
          return navigator.share({ url });
        } else {
          return callback();
        }
      } catch {
        return callback();
      }
    },
    [Platform]
  );

  return { platformShare, platformShareCause };
};

export { usePlatformShare };
